"use client"

import { type ProductNoticesCopy } from "@/app/(site)/[locale]/checkout/CheckoutContent/_utils/productNoticesCopy"
import OrderSummaryProductDetails from "@/app/(site)/[locale]/checkout/CheckoutContent/components/OrderSummery/OrderSummaryProductDetails"
import { PriceTotal } from "@/app/(site)/[locale]/checkout/CheckoutContent/components/PriceTotals"
import {
    AccordionContent,
    AccordionIcon,
    AccordionRoot,
    AccordionTrigger
} from "@/app/(site)/_components/Atoms/Accordion"
import { IconPlus } from "@/app/(site)/_components/Atoms/Icons"
import useSWRBasketSessionState from "@/app/(site)/_utils/_hooks/SWR/useSWRBasketSessionState"
import { FREE_SHIPPING_VOUCHER_CODE } from "@/lib/siteconfig/constants"
import { hasFreeShippingVoucher } from "@/lib/utils/checkout/shippingUtils"
import {
    calculateTotalVoucherDiscount,
    extractVouchersFromSelection
} from "@/lib/utils/checkout/voucherUtils"
import { t } from "@/translations"
import { clsx } from "@/src/utils/clsx"

export function OrderSummaryMinified({
    marketLayout,
    langCode,
    showShipping = false,
    productNoticesCopy
}: {
    marketLayout: string
    langCode: string
    showShipping?: boolean
    productNoticesCopy: ProductNoticesCopy
}) {
    const { selection, isLoadingSelection } = useSWRBasketSessionState()
    const rawVouchers = selection?.discounts?.vouchers
    const vouchers = extractVouchersFromSelection(
        Array.isArray(rawVouchers) ? rawVouchers : []
    )
    const filteredVouchers = Array.isArray(vouchers)
        ? vouchers.filter(
              voucher =>
                  voucher.voucher.toLowerCase() !==
                  FREE_SHIPPING_VOUCHER_CODE.toLowerCase()
          )
        : []

    const hasFreeShipping = hasFreeShippingVoucher(filteredVouchers ?? [])
    const items = selection?.items
    const products = Array.isArray(items) ? items : []
    const totalDiscountVoucherDisplay = calculateTotalVoucherDiscount(
        Array.isArray(filteredVouchers) ? filteredVouchers : []
    )
    const totals = selection?.totals

    return (
        <AccordionRoot
            initalState={false}
            accordionId="order-summary-minified"
            accordionName="Order Summary Minified"
            className="overflow-hidden rounded-lg border border-col-border bg-col-bg-soft"
        >
            <CollapsedHeader totals={totals} langCode={langCode} />
            <AccordionContent>
                <div className="flex flex-col gap-24 pt-20">
                    <OrderSummaryProductDetails
                        products={products}
                        langCode={langCode}
                        voucherLoading={false}
                        isLoadingSelection={isLoadingSelection}
                        oneProduct={products.length === 1}
                        hideBasketFunctionality={true}
                        productNoticesCopy={productNoticesCopy}
                    />
                    <PriceTotal
                        langCode={langCode}
                        totals={totals}
                        showShipping={showShipping}
                        filteredVouchers={filteredVouchers ?? []}
                        hasFreeShipping={hasFreeShipping}
                        totalDiscountVoucherDisplay={totalDiscountVoucherDisplay}
                        marketLayout={marketLayout}
                        isLoadingSelection={isLoadingSelection}
                    />
                </div>
            </AccordionContent>
        </AccordionRoot>
    )
}

function CollapsedHeader({ totals, langCode }: { totals: any; langCode: string }) {
    return (
        <AccordionTrigger
            className={clsx("flex w-full items-center justify-between p-12")}
        >
            <div>
                <span className="Trade_Gothic_Text_Bold_14_150_0_700">
                    {t(langCode, "checkout.order_summary.total")}
                    &nbsp;
                </span>
                <span className="Trim_Mono_POC_Text_14_130_-5_500_uppercase">
                    {totals?.grandTotalPrice || "0"}
                </span>
            </div>
            <AccordionIcon
                openIcon={
                    <>
                        <span className="pt-1 Trim_Mono_POC_Text_12_130_-3_400_uppercase">
                            {t(langCode, "checkout.order_summary.hide_details") ||
                                "HIDE DETAILS"}
                        </span>
                        <IconPlus className="h-16 w-16 rotate-45 transition-transform duration-300" />
                    </>
                }
                closeIcon={
                    <>
                        <span className="pt-1 Trim_Mono_POC_Text_12_130_-3_400_uppercase">
                            {t(langCode, "checkout.order_summary.view_details") ||
                                "VIEW DETAILS"}
                        </span>

                        <IconPlus className="h-16 w-16 transition-transform duration-300" />
                    </>
                }
                className="flex h-24 items-center"
            />
        </AccordionTrigger>
    )
}
